import React, { useMemo } from 'react';
import { Text, StyleSheet } from 'react-native';
import { IColumnConfig } from '@/types/admin/dataTable';
import { IFundingTableRow, FundingJournalData } from '@/types/admin/funding';
import { MEMO_LEDGER, MEMO_FEE, MEMO_TOTAL } from '@/utils/admin/fundingUtils';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';
import { FundingSingleAction } from './FundingSingleAction';
import { DesktopOverflowText } from '@/components/common/admin/desktop/DesktopOverflowText';
import { JournalEntryCell } from './JournalEntryCell';
import { formatUtcDate } from '@/utils/admin/date';

const useColumnStyles = () =>
  useThemedStyles(({ components, colors }) =>
    StyleSheet.create({
      propertyText: {
        fontSize: 14,
        fontWeight: '600',
        color: colors.text.primary,
      },
      merchantText: {
        fontSize: 12,
        color: components.admin.dataCell.textMuted,
        textAlign: 'right',
      },
      dateText: {
        fontSize: 12,
        color: colors.text.primary,
      },
      statusText: {
        fontSize: 12,
        fontWeight: '500',
        color: colors.text.primary,
      },
    }),
  );

const PropertyCell: React.FC<{ value: string }> = ({ value }) => {
  const styles = useColumnStyles();
  return (
    <Text style={styles.propertyText} numberOfLines={2}>
      {value}
    </Text>
  );
};

const MerchantCell: React.FC<{ value: string }> = ({ value }) => {
  const styles = useColumnStyles();
  return (
    <Text style={styles.merchantText} numberOfLines={1}>
      {value}
    </Text>
  );
};

const DateCell: React.FC<{ value: Date }> = ({ value }) => {
  const styles = useColumnStyles();
  return <Text style={styles.dateText}>{formatUtcDate(value)}</Text>;
};

const StatusCell: React.FC<{ value: string; isDesktop: boolean }> = ({ value, isDesktop }) => {
  const styles = useColumnStyles();
  if (isDesktop) {
    return <DesktopOverflowText text={value} title="Last Status" numberOfLines={2} />;
  }
  return <Text style={styles.statusText}>{value}</Text>;
};

const InlineLabel: React.FC<{ label: string }> = ({ label }) => {
  const styles = useColumnStyles();
  return <Text style={styles.statusText}>{label}</Text>;
};

export function useFundingTableColumns(
  onReprocessComplete?: (updatedFunding: IFundingTableRow) => void,
  options?: { isDesktop?: boolean },
): IColumnConfig<IFundingTableRow>[] {
  const isDesktop = !!options?.isDesktop;

  return useMemo<IColumnConfig<IFundingTableRow>[]>(
    () => [
      {
        key: 'property',
        label: 'PROPERTY',
        sortable: false,
        filterable: true,
        visible: true,
        mobileVisible: true,
        sortType: 'string',
        filterType: 'multiSelect',
        filterOptions: [],
        renderCell: (value: string) => <PropertyCell value={value} />,
      },
      {
        key: 'merchantId',
        label: 'MERCHANT ID',
        sortable: false,
        filterable: true,
        visible: true,
        mobileVisible: true,
        sortType: 'string',
        filterType: 'multiSelect',
        filterOptions: [],
        renderCell: (value: string) => <MerchantCell value={value} />,
      },
      {
        key: 'fundingDate',
        label: 'FUNDING DATE',
        sortable: false,
        filterable: true,
        visible: true,
        mobileVisible: true,
        sortType: 'date',
        filterType: 'date',
        renderCell: (value: Date) => <DateCell value={value} />,
      },
      {
        key: 'lastStatus',
        label: 'LAST STATUS',
        sortable: false,
        filterable: true,
        visible: true,
        mobileVisible: true,
        sortType: 'string',
        filterType: 'text',
        renderCell: (value: string) => <StatusCell value={value} isDesktop={isDesktop} />,
      },
      {
        key: 'funding_amount',
        label: 'LEDGER',
        sortable: false,
        filterable: false,
        visible: true,
        mobileVisible: true,
        sortType: 'number',
        renderCell: (value: FundingJournalData | null) => (
          <>
            {!isDesktop && <InlineLabel label="Funding: " />}
            <JournalEntryCell journalData={value} memos={[...MEMO_LEDGER]} />
          </>
        ),
      },
      {
        key: 'fee_amount',
        label: 'FEE',
        sortable: false,
        filterable: false,
        visible: true,
        mobileVisible: true,
        sortType: 'number',
        renderCell: (value: FundingJournalData | null) => (
          <>
            {!isDesktop && <InlineLabel label="Fee: " />}
            <JournalEntryCell journalData={value} memos={[...MEMO_FEE]} />
          </>
        ),
      },
      {
        key: 'net_amount',
        label: 'TOTAL FUNDED',
        sortable: false,
        filterable: false,
        visible: true,
        mobileVisible: true,
        sortType: 'number',
        renderCell: (value: FundingJournalData | null) => (
          <>
            {!isDesktop && <InlineLabel label="Total: " />}
            <JournalEntryCell journalData={value} memos={[...MEMO_TOTAL]} />
          </>
        ),
      },
      {
        key: 'actions',
        label: 'ACTIONS',
        sortable: false,
        filterable: false,
        visible: true,
        mobileVisible: true,
        sortType: 'string',
        renderCell: (value: number, row: IFundingTableRow) => (
          <FundingSingleAction
            funding={row}
            onReprocessComplete={onReprocessComplete || (() => {})}
          />
        ),
      },
    ],
    [isDesktop, onReprocessComplete],
  );
}
